import React from 'react';
import { Box, Typography } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import {
    BenchmarkRun, ExecutiveSummaryResponse, TypologyPerformanceResponse
} from '../../services/benchmarkService';
import { MetricInfo, NotComputed, PanelError, PanelSkeleton, SectionCard, StatBlock } from './BenchmarkPrimitives';
import { DEFINITIONS, count, decimal, hasValue } from './BenchmarkVocabulary';
import { EvidenceChip, QualifiedBadge, WithheldNotice } from './BenchmarkQualifiers';
import { isIndicative } from './benchmarkEvidence';
import {
    NO_WITHHELD, PANEL_FAMILIES, WithheldIndex, qualificationsFor, suppressionReasons
} from './withheldMetrics';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The Evidence tab's model-validation panels.
 *
 * Everything a Model Risk Management reviewer needs and an AML operations lead
 * does not: discrimination, intervals, stability across periods, and the scope
 * of the claim. It lives behind a tab rather than at the top of the page, which
 * is where "Risk Separation (ROC AUC) 0.890" used to lead.
 *
 * Nothing here is asserted unless the run recorded it.
 */

export const RankingQualityPanel: React.FC<{
    summary: ExecutiveSummaryResponse | null;
    run: BenchmarkRun | null;
    /** What the run withheld or qualified. */
    withheld?: WithheldIndex;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}> = ({ summary, run, withheld = NO_WITHHELD, loading, error, onRetry }) => {
    if (error) {
        return (
            <SectionCard title="Ranking quality" icon={<ScienceOutlinedIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={280} />;
    if (!summary) return null;

    const rq = summary.kpis.rankingQuality;
    const available = summary.availability.rankingQuality === 'computed';
    const evidence = rq.evidence ?? summary.evidence;
    const indicative = isIndicative(evidence);

    const rankingWithheld = suppressionReasons(withheld, PANEL_FAMILIES.rankingQuality);
    // The comparison with the incumbent is its own family: a run can rank
    // soundly and still decline to compare, e.g. over a synthetic KYC fixture.
    const headToHeadWithheld = suppressionReasons(withheld, PANEL_FAMILIES.headToHead);
    const qualifications = qualificationsFor(withheld, [...PANEL_FAMILIES.rankingQuality, ...PANEL_FAMILIES.headToHead]);
    const delta = hasValue(rq.delta) ? `${rq.delta > 0 ? '+' : ''}${decimal(rq.delta)}` : '—';

    return (
        <SectionCard
            title="Ranking quality"
            subtitle="How reliably genuine cases are ranked above innocent ones, across the whole queue."
            icon={<ScienceOutlinedIcon fontSize="small" />}
            accent="#38BDF8"
            badges={
                <>
                    <EvidenceChip evidence={evidence} />
                    <QualifiedBadge qualifications={qualifications} />
                </>
            }
        >
            {rankingWithheld.length > 0 ? (
                <WithheldNotice reasons={rankingWithheld} />
            ) : !available ? (
                <NotComputed detail="This run recorded no discrimination metrics." />
            ) : (
                <>
                    <Box className={`grid grid-cols-1 ${headToHeadWithheld.length || indicative ? 'sm:grid-cols-1' : 'sm:grid-cols-3'} gap-5`}>
                        <StatBlock
                            label="With the agent"
                            value={decimal(rq.rocAuc)}
                            technicalName="ROC AUC"
                            definition={DEFINITIONS.rankingQuality}
                            accent="#10B981"
                            indicative={indicative}
                        />
                        {/*
                          On a small sample the margin over the incumbent is not a
                          headline: a perfect score over eight cases says little,
                          and "+0.44 vs your rules" would read as a result.
                        */}
                        {!headToHeadWithheld.length && !indicative && (
                            <>
                                <StatBlock
                                    label="With your current rules"
                                    value={decimal(rq.baselineRocAuc)}
                                    technicalName="Baseline ROC AUC"
                                    accent="#94A3B8"
                                />
                                <StatBlock
                                    label="Improvement"
                                    value={delta}
                                    technicalName="ROC AUC delta"
                                    accent="#FFDE59"
                                />
                            </>
                        )}
                    </Box>

                    {!headToHeadWithheld.length && indicative && (hasValue(rq.baselineRocAuc) || hasValue(rq.delta)) && (
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 1.5 }}>
                            Your current rules scored {decimal(rq.baselineRocAuc)} on the same cases ({delta}). Too few cases to read the difference as an improvement.
                        </Typography>
                    )}

                    {headToHeadWithheld.length > 0 ? (
                        <Box className="mt-5">
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 1 }}>
                                Comparison with your current rules
                            </Typography>
                            <WithheldNotice compact reasons={headToHeadWithheld} />
                        </Box>
                    ) : (
                    <Box className="mt-5 pt-4 border-t border-line">
                        <Box className="flex items-center gap-1.5 mb-1.5">
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft') }}>
                                How confident is this improvement?
                            </Typography>
                            <MetricInfo definition={DEFINITIONS.confidenceInterval} />
                        </Box>

                        {/*
                          The interval, the resample count and the significance claim are
                          each shown only when recorded. The previous build printed a
                          95% CI of [0.3027, 0.3658], "B = 2,000 resamples" and
                          "p < 0.001" as fixed text, and defaulted significance to true.
                        */}
                        {rq.confidenceInterval95 ? (
                            <>
                                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body') }}>
                                    95% interval: {decimal(rq.confidenceInterval95[0])} to {decimal(rq.confidenceInterval95[1])}
                                    {hasValue(rq.bootstrapResamples) && ` (${count(rq.bootstrapResamples)} bootstrap resamples)`}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8125rem', color: rq.statisticallySignificant ? ink('#10B981') : ink('#FBBF24'), mt: 0.75 }}>
                                    {rq.statisticallySignificant
                                        ? 'The interval lies entirely on one side of zero, so the difference is unlikely to be chance.'
                                        : 'The interval includes zero, so this run does not establish a difference.'}
                                </Typography>
                            </>
                        ) : (
                            <NotComputed compact detail="No confidence interval was recorded, so the result's precision cannot be stated." />
                        )}
                    </Box>
                    )}

                    {run?.populationFrame && (
                        <Box className="mt-4 pt-4 border-t border-line">
                            <Box className="flex items-center gap-1.5">
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft') }}>
                                    What this test covers
                                </Typography>
                                <MetricInfo definition={DEFINITIONS.populationFrame} />
                            </Box>
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.75, lineHeight: 1.6 }}>
                                {run.populationFrame === 'alerted_only'
                                    ? 'Alerts your monitoring already raised. This measures re-ordering within that caseload and says nothing about activity that never triggered an alert.'
                                    : `Population frame: ${run.populationFrame}.`}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </SectionCard>
    );
};

export const StabilityPanel: React.FC<{
    performance: TypologyPerformanceResponse | null;
    withheld?: WithheldIndex;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}> = ({ performance, withheld = NO_WITHHELD, loading, error, onRetry }) => {
    if (error) {
        return (
            <SectionCard title="Does it hold up over time?" icon={<TimelineOutlinedIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={260} />;
    if (!performance) return null;

    const epochs = performance.epochStability;
    const scored = epochs.filter((e) => hasValue(e.agentRocAucTP));
    // Stated only when there are at least two periods to compare. The previous
    // panel asserted "2024H1 (0.898) vs H2 (0.883)" from string literals.
    const spread = scored.length >= 2
        ? Math.max(...scored.map((e) => e.agentRocAucTP as number)) - Math.min(...scored.map((e) => e.agentRocAucTP as number))
        : null;

    return (
        <SectionCard
            title="Does it hold up over time?"
            subtitle="Performance measured separately in each evaluation period."
            icon={<TimelineOutlinedIcon fontSize="small" />}
            accent="#FBBF24"
            badges={<QualifiedBadge qualifications={qualificationsFor(withheld, PANEL_FAMILIES.stability)} />}
        >
            {suppressionReasons(withheld, PANEL_FAMILIES.stability).length > 0 ? (
                <WithheldNotice reasons={suppressionReasons(withheld, PANEL_FAMILIES.stability)} />
            ) : epochs.length === 0 ? (
                <NotComputed detail="This run recorded no per-period breakdown, so stability cannot be assessed." />
            ) : (
                <>
                    <Box className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {epochs.map((epoch) => (
                            <Box key={epoch.epoch} className="rounded-xl bg-page/50 border border-line p-4">
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: dsVar('fg-body') }}>
                                    {epoch.epoch}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mb: 1.5 }}>
                                    {count(epoch.sampleSize)} alerts
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    Ranking quality {decimal(epoch.agentRocAucTP)}
                                    {hasValue(epoch.baselineRocAucTP) && ` vs ${decimal(epoch.baselineRocAucTP)} today`}
                                </Typography>
                                {hasValue(epoch.agentRocAucSAR) && (
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.5 }}>
                                        For SAR outcomes {decimal(epoch.agentRocAucSAR)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {spread !== null && (
                        <Box className="mt-4 flex items-start gap-1.5">
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.6 }}>
                                Performance varies by {decimal(spread)} across the {scored.length} periods measured.
                                {spread < 0.05
                                    ? ' That is a narrow spread, so the result does not appear to depend on the period tested.'
                                    : ' That is a wide spread — worth checking before relying on the result going forward.'}
                            </Typography>
                            <MetricInfo definition={DEFINITIONS.stability} />
                        </Box>
                    )}
                </>
            )}
        </SectionCard>
    );
};
